import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config.js';
import { useTenant, useBrand } from '../tenant/TenantProvider.jsx';
import Footer from '../components/Footer.jsx';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(null); // email it was sent to
  const [resetting, setResetting] = useState(false);
  const nav = useNavigate();
  const { gym } = useTenant();
  const brand = useBrand();
  const isBranded = !!gym?.name && gym.name !== 'TrainForge';
  const logoSrc = brand.logoUrl || '/trainforge-logo.svg';
  const displayName = isBranded ? gym.name.toUpperCase() : 'TRAINFORGE';

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      const tokenResult = await cred.user.getIdTokenResult(true);
      const role = tokenResult.claims?.role || 'member';
      const dest = role === 'platform-admin' ? '/platform'
        : role === 'owner' ? '/admin'
        : role === 'trainer' ? '/trainer' : '/app';
      nav(dest, { replace: true });
    } catch (e) {
      setErr(e.message.replace('Firebase: ', ''));
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setErr(null);
    if (!email.trim()) { setErr('Enter your email above first.'); return; }
    setResetting(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetSent(email.trim());
    } catch (e) {
      setErr(e.message.replace('Firebase: ', ''));
    } finally {
      setResetting(false);
    }
  }

  if (resetSent) {
    return (
      <div className="centered" style={{ flexDirection: 'column', gap: '20px', padding: '24px' }}>
        <img src={logoSrc} alt={displayName} width={88} height={88} style={{ borderRadius: 12, objectFit: 'cover' }} />
        <div className="wordmark" style={{ fontSize: '1.6rem' }}>{displayName}</div>
        <div style={{ width: '100%', maxWidth: 380, background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
          <h2 style={{ marginTop: 0 }}>Check your email</h2>
          <p style={{ color: 'var(--text-dim)' }}>
            We sent a password-reset link to <b style={{ color: 'var(--text)' }}>{resetSent}</b>. The link expires in 1 hour. If you don't see it, check your spam folder.
          </p>
          <button onClick={() => setResetSent(null)} style={{ width: '100%', marginTop: 8 }}>Back to sign in</button>
          <button
            onClick={reset}
            disabled={resetting}
            style={{ width: '100%', marginTop: 8, background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 13, minHeight: 36 }}
          >
            {resetting ? 'Resending…' : "Didn't get it? Resend"}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="centered" style={{ flexDirection: 'column', gap: '24px', padding: '24px' }}>
      <img src={logoSrc} alt={displayName} width={88} height={88} style={{ borderRadius: 12, objectFit: 'cover' }} />
      <div className="wordmark" style={{ fontSize: '1.6rem' }}>{displayName}</div>
      {isBranded && brand.tagline && (
        <div style={{ fontSize: 12, letterSpacing: '0.3em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: -16 }}>
          {brand.tagline}
        </div>
      )}
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          type="email"
          placeholder="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoCapitalize="none"
          required
        />
        <input
          type="password"
          placeholder="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        {err && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{err}</div>}
        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={resetting}
          style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: 13, padding: 4, minHeight: 0 }}
        >
          {resetting ? 'Sending…' : 'Forgot / set password'}
        </button>
      </form>
      {!isBranded && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>
          Don't have an account?{' '}
          <Link to="/contact" style={{ color: 'var(--accent)', fontWeight: 600 }}>
            Request beta access
          </Link>
        </div>
      )}
      <Footer />
    </div>
  );
}
